/* Copyright (c) 2026 PearlCrm Pty Ltd. All rights reserved. */

/**
 * OAuth consent: "<App> wants to access your PearlCRM account".
 *
 * Reached from GET /oauth/authorize, which has already validated the app, its
 * redirect address and the requested scopes, and passes them here as a signed
 * `req` handle. This page only shows what the handle says and sends the
 * person's decision back; the backend re-reads everything from the signature,
 * so nothing on this page can widen what the app gets.
 *
 * Rendered without the app shell: it is a decision screen, and the sidenav
 * would invite the person to wander off mid-flow.
 */

import React, { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Alert, Avatar, Card, CircularProgress, Divider, List, ListItem, ListItemIcon, ListItemText } from "@mui/material";
import { CheckCircleOutline, Lock } from "@mui/icons-material";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import { useAuth } from "auth/Auth";
import colors from "styles/tokens/colors";
import { getApiErrorMessage } from "utils/apiErrors";

export default function Consent() {
  const [params] = useSearchParams();
  const req = params.get("req") || "";
  const { axiosInstance, isAuthReady, isAuthenticated, user } = useAuth();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const autoSubmitted = useRef(false);

  const decide = async (decision) => {
    setBusy(true);
    setError("");
    try {
      const res = await axiosInstance.post("/oauth/consent", { req, decision });
      // The client's own redirect URI, validated by the backend.
      window.location.assign(res.data.redirect_to);
    } catch (e) {
      setError(getApiErrorMessage(e, "Could not complete the authorisation."));
      setBusy(false);
    }
  };

  useEffect(() => {
    if (!isAuthReady || !isAuthenticated) return;
    if (!req) {
      setError("This page needs an authorisation request. Start again from the app you came from.");
      return;
    }
    axiosInstance
      .get("/oauth/consent/request", { params: { req } })
      .then((res) => setInfo(res.data))
      .catch((e) => setError(getApiErrorMessage(e, "This authorisation request could not be read.")));
  }, [axiosInstance, isAuthReady, isAuthenticated, req]);

  // Already approved with at least this access: go straight back to the app.
  useEffect(() => {
    if (info?.previously_granted && info.scopes.length && !autoSubmitted.current) {
      autoSubmitted.current = true;
      decide("approve");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [info]);

  const name = info?.client?.name || "This app";

  // nginx refuses to let this page be framed; if that header were ever lost,
  // refuse here too rather than show an Allow button inside someone else's page.
  if (typeof window !== "undefined" && window.top !== window.self) {
    return (
      <MDBox p={4}>
        <MDTypography variant="body2">
          For your security this page cannot be shown inside another site. Open it in its own window.
        </MDTypography>
      </MDBox>
    );
  }

  return (
    <MDBox minHeight="100vh" display="flex" alignItems="center" justifyContent="center" px={2}
      sx={{ backgroundColor: colors.background?.default || "#f5f6fa" }}>
      <Card sx={{ maxWidth: 480, width: "100%", p: 4, borderRadius: "12px", boxShadow: "none",
        border: `1px solid ${colors.callCenter.cardBorder}` }}>
        {!info && !error && (
          <MDBox py={6} textAlign="center"><CircularProgress /></MDBox>
        )}

        {error && (
          <>
            <MDTypography variant="h5" fontWeight="medium" mb={2}>Authorisation problem</MDTypography>
            <Alert severity="error">{error}</Alert>
          </>
        )}

        {info && !error && (
          <>
            <MDBox display="flex" alignItems="center" gap={2} mb={2}>
              <Avatar src={info.client.logo_url || undefined} alt="" sx={{ width: 48, height: 48 }}>
                {name.charAt(0)}
              </Avatar>
              <MDBox>
                <MDTypography variant="h5" fontWeight="medium">{name}</MDTypography>
                <MDTypography variant="caption" color="text">will return you to {info.redirect_host}</MDTypography>
              </MDBox>
            </MDBox>

            <MDTypography variant="body2" mb={1}>
              <strong>{name}</strong> wants to access PearlCRM as you
              {user?.email ? ` (${user.email})` : ""}. It will be able to:
            </MDTypography>

            {info.scopes.length > 0 ? (
              <List dense sx={{ maxHeight: 260, overflowY: "auto" }}>
                {info.scopes.map((s) => (
                  <ListItem key={s.scope} disableGutters>
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <CheckCircleOutline fontSize="small" sx={{ color: colors.status.success.dot }} />
                    </ListItemIcon>
                    <ListItemText primary={s.description} primaryTypographyProps={{ variant: "body2" }} />
                  </ListItem>
                ))}
              </List>
            ) : (
              <Alert severity="warning" sx={{ my: 1 }}>
                Your role does not allow any of the access this app asked for.
              </Alert>
            )}

            {info.withheld.length > 0 && (
              <MDTypography variant="caption" color="text" display="block" mb={1}>
                It also asked for {info.withheld.length} permission{info.withheld.length === 1 ? "" : "s"} your
                role does not have; it will not get those.
              </MDTypography>
            )}

            <Divider sx={{ my: 2 }} />
            <MDBox display="flex" alignItems="flex-start" gap={1} mb={3}>
              <Lock fontSize="small" sx={{ color: colors.status.neutral.dot, mt: 0.25 }} />
              <MDTypography variant="caption" color="text">
                It can never do more than your own role allows. You can disconnect it at any time from
                Settings &rarr; Connected apps.
              </MDTypography>
            </MDBox>

            <MDBox display="flex" gap={1} justifyContent="flex-end">
              <MDButton variant="outlined" color="secondary" disabled={busy} onClick={() => decide("deny")}>
                Cancel
              </MDButton>
              <MDButton variant="gradient" color="info" disabled={busy || info.scopes.length === 0}
                onClick={() => decide("approve")}>
                {busy ? "Working…" : "Allow"}
              </MDButton>
            </MDBox>
          </>
        )}
      </Card>
    </MDBox>
  );
}
